'use client';

import { useStore } from '@/store/store';
import { realityCheckService } from '@/logic/reality-check/reality-check-service';
import { useEffect, useCallback } from 'react';
import { sendRealityCheckMessageToGameWindows } from '@/utils/message-broker/send-to-game-windows';
import { POPUPS } from '@/app/components/popup/constants';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { isXsell } from '@/logic/platform';

export const useRealityCheck = () => {
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const accountID = useStore((state) => state.userContext.accountSettings.accountID);
    const setRealityCheckData = useStore((state) => state.realityCheck.setRealityCheckData);
    const showModal = useStore((state) => state.realityCheck.showModal);
    const hideModal = useStore((state) => state.realityCheck.hideModal);
    const reset = useStore((state) => state.realityCheck.reset);
    const closePopup = useStore((state) => state.popup.closePopup);
    const removePopupByType = useStore((state) => state.popup.removePopupByType);
    const queuePopup = useStore((state) => state.popup.queuePopup);
    const isIos = useStore((state) => state.globals.isIos);
    const gameWindows = useStoreWithEqualityFn(
        useStore,
        (s) => s.gameWindows,
        (a, b) => a === b
    );

    const showModalWithQueue = useCallback(() => {
        showModal();
        queuePopup({ type: POPUPS.REALITY_CHECK });
    }, [showModal, queuePopup]);

    useEffect(() => {
        // Handle logged out state
        if (!isLoggedIn) {
            if (realityCheckService.isServiceInitialized()) {
                realityCheckService.destroy();
                reset();
            }
            return () => {};
        }

        if (isXsell() || isIos) {
            if (realityCheckService.isServiceInitialized()) {
                realityCheckService.destroy();
                reset();
            }
            return () => {};
        }

        // Initialize service if logged in and have valid account ID
        if (accountID && !realityCheckService.isServiceInitialized()) {
            realityCheckService.initialize(isLoggedIn, accountID, setRealityCheckData, showModalWithQueue);
        }
    }, [isLoggedIn, isIos, accountID, setRealityCheckData, showModalWithQueue, reset]);

    const handleContinue = async () => {
        hideModal();
        closePopup();
        removePopupByType(POPUPS.REALITY_CHECK);

        sendRealityCheckMessageToGameWindows(gameWindows);

        realityCheckService.continueRealityCheck();
    };

    return {
        handleContinue
    };
};
